// Settings → Your look: profile photo, or a character if you'd rather not use one, and a theme colour.
import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Check, ImagePlus, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const BASE = process.env.REACT_APP_BACKEND_URL || "";

export default function YourLook() {
  const { user, refresh } = useAuth();
  const [looks, setLooks] = useState(null);
  const [busy, setBusy] = useState("");
  const [color, setColor] = useState(user?.theme_color || "");
  const [character, setCharacter] = useState(user?.avatar_character || "");
  const [avatar, setAvatar] = useState(user?.avatar || "");
  const file = useRef(null);

  useEffect(() => { api("/looks", { auth: false }).then(setLooks).catch(() => setLooks({ colors: [], characters: [] })); }, []);
  useEffect(() => { setColor(user?.theme_color || ""); setCharacter(user?.avatar_character || ""); setAvatar(user?.avatar || ""); }, [user?.theme_color, user?.avatar_character, user?.avatar]);

  const save = async (body, what) => {
    setBusy(what);
    try {
      await api("/auth/me", { method: "PATCH", body });
      await refresh?.();
      toast.success(what === "color" ? "Theme colour saved" : "Profile picture updated");
    } catch (e) {
      toast.error(e.message || "That didn't save. Please try again.");
    } finally {
      setBusy("");
    }
  };

  const pickCharacter = (c) => { setCharacter(c.id); setAvatar(c.url); save({ avatar_character: c.id }, "character"); };
  const pickColor = (c) => { setColor(c.hex); document.documentElement.style.setProperty("--user-accent", c.hex); save({ theme_color: c.hex }, "color"); };

  const upload = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!/^image\//.test(f.type)) { toast.error("Choose a photo (JPG, PNG or WebP)."); return; }
    if (f.size > 15 * 1024 * 1024) { toast.error("That photo is over 15MB. Choose a smaller one."); return; }
    setBusy("photo");
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("kind", "avatar");
      let token = null;
      try { token = localStorage.getItem("fable_web_token"); } catch { /* private mode */ }
      const r = await fetch(BASE + "/api/media/upload", { method: "POST", body: fd, headers: token ? { Authorization: "Bearer " + token, "X-Platform": "web" } : { "X-Platform": "web" } });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || !d.url) throw new Error(d.detail || "Upload failed. Please try again.");
      setAvatar(d.url); setCharacter("");
      await api("/auth/me", { method: "PATCH", body: { avatar: d.url } });
      await refresh?.();
      toast.success("Profile photo updated");
    } catch (err) {
      toast.error(err.message || "Upload failed. Please try again.");
    } finally {
      setBusy("");
    }
  };

  const accent = color || "#E0007A";
  const initial = (user?.full_name || "?").trim().charAt(0).toUpperCase();

  return (
    <div className="space-y-6" data-testid="your-look">
      <section className="rounded-2xl border p-5">
        <div className="flex flex-wrap items-center gap-5">
          <div className="relative h-24 w-24 shrink-0 rounded-full p-1" style={{ background: accent }}>
            {avatar ? (
              <img src={avatar} alt="" className="h-full w-full rounded-full bg-background object-cover" />
            ) : (
              <div className="flex h-full w-full items-center justify-center rounded-full bg-background text-3xl font-bold">{initial}</div>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="font-bold">Profile picture</h2>
            <p className="mt-1 text-sm text-muted-foreground">Use a photo, or pick a character below if you'd rather not show your face. You can change it any time.</p>
            <div className="mt-3 flex flex-wrap gap-2">
              <input ref={file} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={upload} />
              <Button type="button" variant="outline" onClick={() => file.current?.click()} disabled={busy === "photo"} data-testid="look-upload">
                {busy === "photo" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ImagePlus className="mr-2 h-4 w-4" />}Upload a photo
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="rounded-2xl border p-5">
        <h2 className="font-bold">Characters</h2>
        <p className="mt-1 text-sm text-muted-foreground">Tap one to use it as your picture on Fable+, Stage and Relay.</p>
        <div className="mt-4 grid grid-cols-4 gap-3 sm:grid-cols-6 lg:grid-cols-8" role="radiogroup" aria-label="Characters">
          {(looks?.characters || []).map((c) => {
            const on = character === c.id;
            return (
              <button key={c.id} type="button" role="radio" aria-checked={on} aria-label={"Character " + c.id.replace("-", " ")}
                onClick={() => pickCharacter(c)} disabled={!!busy}
                className="relative aspect-square rounded-full p-0.5 transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                style={{ background: on ? accent : "transparent" }} data-testid="look-character">
                <img src={c.url} alt="" loading="lazy" className="h-full w-full rounded-full object-cover" style={{ background: c.bg }} />
                {on && <span className="absolute -right-0.5 -top-0.5 flex h-5 w-5 items-center justify-center rounded-full text-white" style={{ background: accent }}><Check className="h-3 w-3" /></span>}
              </button>
            );
          })}
          {!looks && Array.from({ length: 8 }).map((_, i) => <div key={i} className="aspect-square animate-pulse rounded-full bg-muted" />)}
        </div>
        {looks?.credits?.length ? <p className="mt-4 text-xs text-muted-foreground">{looks.credits.join(" ")}</p> : null}
      </section>

      <section className="rounded-2xl border p-5">
        <h2 className="font-bold">Theme colour</h2>
        <p className="mt-1 text-sm text-muted-foreground">Your colour shows around your picture and on your profile.</p>
        <div className="mt-4 flex flex-wrap gap-3" role="radiogroup" aria-label="Theme colour">
          {(looks?.colors || []).map((c) => {
            const on = (color || "").toUpperCase() === c.hex.toUpperCase();
            return (
              <button key={c.key} type="button" role="radio" aria-checked={on} aria-label={c.name} title={c.name}
                onClick={() => pickColor(c)} disabled={!!busy}
                className="flex h-10 w-10 items-center justify-center rounded-full ring-offset-2 ring-offset-background transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                style={{ background: c.hex, boxShadow: on ? "0 0 0 2px var(--background, #fff), 0 0 0 4px " + c.hex : "none" }} data-testid="look-color">
                {on && <Check className="h-4 w-4 text-white" />}
              </button>
            );
          })}
        </div>
      </section>
    </div>
  );
}
